/* aviaf — shared CR/Alerts/Ops components */
(() => {
const { useState, useEffect, useMemo } = React;
const S = () => window.OpsStore;

window.useOps = function useOps() {
  const [v, setV] = useState(0);
  useEffect(() => S().subscribe(setV), []);
  return v;
};
window.useTicker = function useTicker(ms = 30000) {
  const [, setT] = useState(0);
  useEffect(() => { const id = setInterval(() => setT((t) => t + 1), ms); return () => clearInterval(id); }, [ms]);
};

/* ── chips ── */
window.SevChip = function SevChip({ sev }) {
  const legacy = sev === "warning";
  const norm = legacy ? "warn" : sev;
  return <span className={"sev-chip " + norm} title={legacy ? "raw severity: 'warning' (legacy duplicate of warn — source needs taxonomy fix)" : "severity: " + sev} data-legacy={legacy}>{norm.toUpperCase()}{legacy && "*"}</span>;
};
window.ActorChip = function ActorChip({ actor }) {
  const kind = actor === "autopilot" ? "auto" : actor.startsWith("mcp:") ? "mcp" : actor.startsWith("dedup:") ? "dedup" : "human";
  return <span className="actor-chip" data-kind={kind}>{actor}</span>;
};
window.TierChip = function TierChip({ tier }) {
  return <span className="tier-chip" data-tier={tier}>{tier === "destructive" ? "DESTRUCTIVE" : "SAFE"}</span>;
};
window.StatusChip = function StatusChip({ status }) {
  return <span className="st-chip" data-st={status}>{status.toUpperCase()}</span>;
};
window.ModelChip = function ModelChip({ model, note }) {
  return <span className="model-chip" data-m={model} title={note || undefined}>{model}{note ? "↗" : ""}</span>;
};
window.Age = function Age({ ts, prefix, suffix }) {
  window.useTicker(20000);
  return <span className="tnum">{prefix}{window.fmtAge(ts)}{suffix}</span>;
};
window.Meter = function Meter({ used, cap, label, small }) {
  const pct = cap ? Math.min(1, used / cap) : 0;
  const state = pct >= 1 ? "stop" : pct >= 0.8 ? "warn" : "ok";
  return (
    <div className={"bud-meter" + (small ? " sm" : "")} data-state={state} title={label}>
      <div className="bm-bar"><div className="bm-fill" style={{ width: (pct * 100).toFixed(1) + "%" }} /></div>
      {!small && <div className="bm-lbl"><span>{label}</span><span className="tnum">{pct >= 1 ? "HARD-STOP" : Math.round(pct * 100) + "%"}</span></div>}
    </div>
  );
};

/* ── typed / plain confirm (destructive tier, §3.5) ── */
window.OpsConfirm = function OpsConfirm({ title, sub, body, requireText, confirmLabel, onConfirm, onClose }) {
  const [txt, setTxt] = useState("");
  const ok = !requireText || txt === requireText;
  return (
    <div className="se-backdrop centered" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="se-confirm-card" style={{ width: 480 }}>
        <div className="cf-head">
          <div className="cf-icon">!</div>
          <div><div className="cf-title">{title}</div><div className="cf-sub">{sub}</div></div>
          <button className="se-close" onClick={onClose}>×</button>
        </div>
        <div className="cf-body">
          {body}
          {requireText && (
            <div style={{ marginTop: 14 }}>
              <div className="mono" style={{ fontSize: 10.5, color: "var(--muted)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 5 }}>type <b style={{ color: "var(--neg)" }}>{requireText}</b> to confirm</div>
              <input className="ops-input" autoFocus value={txt} onChange={(e) => setTxt(e.target.value)} placeholder={requireText} />
            </div>
          )}
        </div>
        <div className="ss-foot">
          <span className="ss-foot-hint mono">journalled to techops_actions</span>
          <span style={{ flex: 1 }} />
          <button className="ss-btn ghost" onClick={onClose}>Cancel</button>
          <button className="ss-btn danger" disabled={!ok} style={!ok ? { opacity: 0.35, cursor: "not-allowed" } : null} onClick={() => { if (ok) { onConfirm(); onClose(); } }}>{confirmLabel || "Approve"}</button>
        </div>
      </div>
    </div>
  );
};

/* ── delivery trail for a crit (escalation state, §4.2) ── */
window.DeliveryTrail = function DeliveryTrail({ incidentId }) {
  useOps();
  const acts = S().actions.filter((r) => r.incident === incidentId);
  const has = (fn) => acts.some(fn);
  const steps = [
    { l: "autopilot trigger", on: has((r) => r.kind === "auto_trigger") },
    { l: "bridge delivered", on: has((r) => r.kind === "auto_trigger" && /bridge/.test(r.outcome || "")) },
    { l: "node session", on: has((r) => r.actor === "mcp:aviaf-claude") },
    { l: "slack posted", on: has((r) => r.kind === "notify") },
    { l: "operator ack", on: S().alerts.some((a) => a.incident === incidentId && a.sev === "crit" && a.ack && window.isHumanActor(a.ack.by)) },
  ];
  return (
    <div className="dtrail mono">
      <span className="dt-cap">delivery</span>
      {steps.map((s, i) => (
        <span key={i} className="dt-step" data-on={s.on}>{s.on ? "✓" : "○"} {s.l}{i < steps.length - 1 ? "" : ""}</span>
      ))}
    </div>
  );
};

/* ── incident thread: alerts + journal rows, one chronology (PagerDuty pattern) ── */
window.IncidentThread = function IncidentThread({ incidentId, compact }) {
  useOps(); window.useTicker(20000);
  const rows = window.opsIncidentRows(incidentId);
  const meta = window.opsIncidentMeta(incidentId);
  if (!meta) return null;
  return (
    <div className="thr">
      {!compact && (
        <div className="thr-head">
          <div>
            <span className={"inc-dot " + (meta.status === "open" ? (meta.openCrit ? "neg" : "warn") : "ok")} />
            <span className="mono" style={{ fontWeight: 500 }}>{meta.id}</span>
            <span className="mono muted" style={{ marginLeft: 10 }}>{meta.title}</span>
          </div>
          <div className="mono muted" style={{ fontSize: 11, display: "flex", gap: 14, flexWrap: "wrap" }}>
            <span>{meta.status === "open" ? <>open <window.Age ts={meta.opened} /></> : <>closed · ran {window.fmtAge(meta.opened + (Date.now() - (meta.closed || Date.now())))}</>}</span>
            <span>last update <window.Age ts={meta.lastAct} suffix=" ago" /></span>
            {meta.stale && <span className="stale-chip">STALE — no action for {window.fmtAge(meta.lastAct)}</span>}
          </div>
        </div>
      )}
      <window.DeliveryTrail incidentId={incidentId} />
      <div className="thr-rows">
        {rows.map((x, i) => x.type === "alert" ? (
          <div key={"a" + x.a.pk} className="thr-row" data-alert="true">
            <div className="thr-t mono">{window.fmtTs(x.ts)}</div>
            <div className="thr-rail"><span className="thr-node" data-sev={x.a.sev === "crit" ? "crit" : "warn"} /></div>
            <div className="thr-body">
              <div className="thr-l1">
                <window.SevChip sev={x.a.sev} />
                <span className="mono" style={{ fontWeight: 500 }}>{x.a.source}/{x.a.code}</span>
                <span className="mono muted">#{x.a.pk}</span>
                {x.a.repeats && x.a.repeats.n > 1 && <span className="rep-chip">×{x.a.repeats.n} since {window.fmtTs(x.a.repeats.since)}</span>}
              </div>
              <div className="thr-msg">{x.a.msg}</div>
              <div className="thr-meta mono">
                {x.a.instance && <span>{x.a.instance}</span>}{x.a.account && <span>{x.a.account}</span>}
                {x.a.ack ? <span className="ok">acked by {x.a.ack.by} · {window.fmtTs(x.a.ack.ts)}{x.a.ack.reason ? ` · "${x.a.ack.reason}"` : ""}</span> : <span className="neg">UNACKED{x.a.sev === "crit" ? " — operator only" : ""}</span>}
              </div>
            </div>
          </div>
        ) : (
          <div key={"r" + x.r.pk} className="thr-row" data-flag={x.r.flagged ? "true" : undefined}>
            <div className="thr-t mono">{window.fmtTs(x.ts)}</div>
            <div className="thr-rail"><span className="thr-node" data-st={x.r.status} /></div>
            <div className="thr-body">
              <div className="thr-l1">
                <window.ActorChip actor={x.r.actor} />
                <span className="mono" style={{ fontWeight: 500 }}>{x.r.kind}</span>
                {x.r.tier === "destructive" && <window.TierChip tier={x.r.tier} />}
                <window.StatusChip status={x.r.status} />
              </div>
              {(x.r.outcome || x.r.args) && (
                <div className="thr-msg">
                  {x.r.outcome || <span className="mono muted">{JSON.stringify(x.r.args)}</span>}
                </div>
              )}
              {x.r.notes && <div className="thr-note">{x.r.notes}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── incident drawer (global, opened from any tab) ── */
window.IncidentDrawer = function IncidentDrawer() {
  const v = useOps();
  const id = S().drawer;
  if (!id) return null;
  const meta = window.opsIncidentMeta(id);
  return (
    <div className="se-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) { S().drawer = null; S()._emit(); } }}>
      <div className="se-sheet" style={{ width: 640 }}>
        <div className="ss-hero" style={{ paddingBottom: 14 }}>
          <div className="ss-kicker">incident<span className="ss-arrow">›</span><span className="ss-acct">{id}</span></div>
          <div className="ss-title-row">
            <div>
              <h2 className="ss-title" style={{ fontSize: 17 }}>{meta ? meta.title : id}</h2>
              <div className="ss-sub">
                {meta && meta.status === "open" ? <span className="neg">OPEN · <window.Age ts={meta.opened} /></span> : <span className="ok">CLOSED</span>}
                <span className="dotsep">·</span><span>{meta ? meta.actionCount : 0} journalled actions</span>
                {meta && meta.instance && <><span className="dotsep">·</span><span>{meta.instance}</span></>}
              </div>
            </div>
            <button className="se-close" onClick={() => { S().drawer = null; S()._emit(); }}>×</button>
          </div>
        </div>
        <div className="ss-body" style={{ padding: "14px 18px" }}>
          <window.IncidentThread incidentId={id} compact={false} />
          {meta && meta.status === "open" && (
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <window.Btn onClick={() => S().resolveIncident(id)}>Resolve incident</window.Btn>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
window.openIncidentDrawer = (id) => { S().drawer = id; S()._emit(); };

/* ── decision card (Ops queue + candidate promotions) ── */
window.DecisionCard = function DecisionCard({ r }) {
  const [confirm, setConfirm] = useState(false);
  const [denying, setDenying] = useState(false);
  const [reason, setReason] = useState("");
  const title = r.kind === "restart" ? `Restart ${r.args && r.args.unit ? r.args.unit : r.instance}`
    : r.kind === "candidate_promote" ? `Promote ${r.args.candidate} → live-recommended`
    : r.kind === "budget_change" ? `Budget: ${r.args.lane} ${window.fmtTok(r.args.from)} → ${window.fmtTok(r.args.to)} tokens/${(r.args.field || "").includes("night") ? "night" : "day"}`
    : r.kind === "recommendation" ? (r.args && r.args.change ? `Arm: ${r.args.detail}` : "Recommendation")
    : r.kind === "research_request" ? `Research request: “${r.args.question}”`
    : r.kind;
  const isReq = r.kind === "research_request";
  const requireText = r.kind === "restart" ? (r.instance || "") : r.kind === "candidate_promote" ? r.args.candidate : null;
  return (
    <div className="dq-card" data-tier={r.tier}>
      <div className="dq-l1">
        <window.TierChip tier={r.tier} />
        <span className="dq-title">{title}</span>
        <span style={{ flex: 1 }} />
        <span className="mono muted" style={{ fontSize: 11, whiteSpace: "nowrap" }}>#{r.pk} · <window.Age ts={r.ts} suffix=" ago" /></span>
      </div>
      <div className="dq-meta mono">
        <window.ActorChip actor={r.actor} />
        {r.instance && <span className="dq-ctx">{r.instance}</span>}
        {r.run && <a className="dq-ctx" href="#/research" title={r.run}>run: {r.run.length > 34 ? r.run.slice(0, 34) + "…" : r.run}</a>}
        {r.incident && <a className="dq-ctx" href="#/ops" onClick={(e) => { e.preventDefault(); window.openIncidentDrawer(r.incident); }}>incident {r.incident}</a>}
        {r.trigger && <span className="dq-ctx">alert #{r.trigger}</span>}
      </div>
      {(r.notes || r.outcome) && <div className="dq-body">{r.notes || r.outcome}</div>}
      {r.status === "proposed" ? (
        <div className="dq-actions">
          {isReq ? (
            <>
              <span className="mono muted" style={{ fontSize: 11 }}>queued — node picks it up at next tick ({window.fmtTs(window.nextTickUtc())})</span>
              <span style={{ flex: 1 }} />
              <window.Btn onClick={() => S().deny(r.pk, "request withdrawn")}>Withdraw</window.Btn>
            </>
          ) : denying ? (
            <>
              <input className="ops-input" style={{ flex: 1 }} autoFocus placeholder="reason (required, journalled)" value={reason} onChange={(e) => setReason(e.target.value)} />
              <window.Btn kind="danger" onClick={() => { if (reason.trim()) { S().deny(r.pk, reason.trim()); } }}>Deny</window.Btn>
              <window.Btn onClick={() => setDenying(false)}>Cancel</window.Btn>
            </>
          ) : (
            <>
              <window.Btn kind="primary" onClick={() => { r.tier === "destructive" ? setConfirm(true) : S().approve(r.pk); }}>Approve</window.Btn>
              <window.Btn onClick={() => setDenying(true)}>Deny…</window.Btn>
              {r.tier === "destructive" && <span className="mono muted" style={{ fontSize: 10.5 }}>second confirmation required</span>}
            </>
          )}
        </div>
      ) : (
        <div className="dq-actions"><window.StatusChip status={r.status} />{r.outcome && <span className="mono muted" style={{ fontSize: 11 }}>{r.outcome}</span>}</div>
      )}
      {confirm && (
        <window.OpsConfirm
          title={title} sub={`#${r.pk} · ${r.kind} · destructive tier`}
          requireText={requireText}
          body={<div className="ss-prose" style={{ fontSize: 12.5 }}>
            {r.kind === "restart" && <>Dispatches a COMMAND over the instance socket. {S().fleet.find((f) => f.id === r.instance && f.state === "silent") ? <b className="neg">Instance socket is DOWN — command will be executed via host-side recovery after approval.</b> : "Instance socket is live."}</>}
            {r.kind === "candidate_promote" && <>Marks the candidate <b>live-recommended</b>. Nothing goes live automatically — engine-side deployment remains a manual human act.</>}
            {r.kind === "budget_change" && <>Applies the new budget and journals a policy_change with old→new. The node receives it on its next policy-sync.</>}
            {r.kind === "recommendation" && <>Arms an upward model override — costs more per session; the lane budget still hard-stops at 100%.</>}
          </div>}
          onConfirm={() => S().approve(r.pk)} onClose={() => setConfirm(false)} />
      )}
    </div>
  );
};

/* ── global crit banner (§3.3 — every tab) + fleet dot ── */
window.CritBanner = function CritBanner() {
  useOps(); window.useTicker(15000);
  const open = S().incidents.map((i) => window.opsIncidentMeta(i.id)).filter((m) => m && m.status === "open" && m.openCrit);
  if (!open.length) return null;
  return (
    <div className="critbar-stack">
      {open.map((m) => {
        const crit = S().alerts.find((a) => a.incident === m.id && a.sev === "crit" && !a.ack);
        const ackDue = crit && (Date.now() - crit.ts) / 60000 > S().slaMinutes;
        return (
          <div key={m.id} className="critbar" role="alert">
            <span className="cb-dot" />
            <span className="mono cb-id">{m.id}</span>
            <span className="cb-title">{m.title}</span>
            <span className="mono cb-meta">open <window.Age ts={m.opened} /> · last update <window.Age ts={m.lastAct} suffix=" ago" /></span>
            {m.stale && <span className="stale-chip">STALE — no action for {window.fmtAge(m.lastAct)}</span>}
            {ackDue && <span className="sla-chip">ESCALATION DUE — crit unacked &gt;{S().slaMinutes}m</span>}
            <span style={{ flex: 1 }} />
            {crit && <button className="cb-ack" onClick={() => S().ackAlert(crit.pk)}>ACK #{crit.pk}</button>}
            <button className="cb-open" onClick={() => window.openIncidentDrawer(m.id)}>incident →</button>
          </div>
        );
      })}
    </div>
  );
};

window.FleetDot = function FleetDot() {
  useOps(); window.useTicker(15000);
  const st = window.opsFleetState();
  const crits = window.opsOpenCrits().length;
  const label = st === "down" ? "instance down" : st === "degraded" ? "degraded" : "all systems";
  return (
    <span className="heartbeat" title={"fleet: " + label + (crits ? ` · ${crits} open crit` : "")}>
      <span className={"dot" + (st === "down" ? " neg" : st === "degraded" ? " warn" : "")} /> {label}{crits > 0 && <b className="neg" style={{ marginLeft: 4 }}>· {crits} crit</b>}
    </span>
  );
};

/* mounted once by App: drawer host */
window.OpsGlobal = function OpsGlobal() {
  useOps();
  return <window.IncidentDrawer />;
};
})();
